import React, { useEffect, useMemo, useState, useRef } from 'react';
import PageContainer from '../PageContainer';
import SearchableSelect from '../components/SearchableSelect';
import './LeadCareerAssociateMapping.css';

// Simple modal for selecting a new associate
function ChangeAssociateModal({ isOpen, onClose, agents, currentAgentId, onConfirm, leadName }) {
  const [selected, setSelected] = useState(currentAgentId);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setSelected(currentAgentId);
    setSearchTerm('');
    setIsDropdownOpen(false);
  }, [currentAgentId, isOpen]);

  // Handle clicks outside the dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setSearchTerm('');
      }
    }

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isDropdownOpen]);

  // Filter agents based on search term
  const filteredAgents = agents.filter(agent =>
    (agent.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (agent.email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get selected agent for display
  const selectedAgent = agents.find(agent => agent.id === selected);

  const handleAgentSelect = (agent) => {
    setSelected(agent.id);
    setSearchTerm('');
    setIsDropdownOpen(false);
  };

  const handleInputFocus = () => {
    setIsDropdownOpen(true);
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-6 w-full max-w-md mx-4 transform transition-all duration-300">
        <div className="flex justify-between items-center mb-5 pb-3 border-b border-slate-100">
          <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            {currentAgentId ? 'Change Associate' : 'Assign Associate'}
          </h3>
          <button className="text-slate-400 hover:text-slate-600 transition-colors text-xl font-bold" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="space-y-4">
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Lead Name</p>
            <p className="text-sm font-bold text-slate-800">{leadName}</p>
          </div>
          
          <div className="relative" ref={dropdownRef}>
            <label className="block text-sm font-bold text-slate-700 mb-1.5">Select associate</label>
            <div className="relative">
              <input
                type="text"
                className="w-full border border-slate-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 px-4 py-2.5 rounded-lg text-sm outline-none transition-all pr-16 bg-white"
                placeholder={selectedAgent ? `${selectedAgent.name} (${selectedAgent.email})` : "Search for an associate..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={handleInputFocus}
              />
              <div className="absolute right-2 top-2 flex items-center gap-1">
                {selectedAgent && !searchTerm && (
                  <button
                    type="button"
                    className="text-slate-400 hover:text-slate-600 p-1"
                    onClick={() => {
                      setSelected(null);
                      setSearchTerm('');
                      setIsDropdownOpen(false);
                    }}
                    title="Clear selection"
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                )}
                <button
                  type="button"
                  className="text-slate-400 hover:text-slate-600 p-1"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  <svg
                    className={`w-4 h-4 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7-7-7-7" />
                  </svg>
                </button>
              </div>
            </div>
            
            {isDropdownOpen && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-slate-100 rounded-lg shadow-lg max-h-60 overflow-y-auto divide-y divide-slate-50">
                <div 
                  className={`px-4 py-2.5 text-xs font-semibold text-slate-400 hover:bg-slate-50 cursor-pointer ${!selected ? 'bg-indigo-50/50 text-indigo-700' : ''}`}
                  onClick={() => {
                    setSelected(null);
                    setSearchTerm('');
                    setIsDropdownOpen(false);
                  }}
                >
                  -- Select an associate --
                </div>
                {filteredAgents.length > 0 ? (
                  filteredAgents.map((agent) => (
                    <div
                      key={agent.id}
                      className={`px-4 py-2 hover:bg-indigo-50/30 cursor-pointer transition-colors ${selected === agent.id ? 'bg-indigo-50 text-indigo-900 font-semibold' : 'text-slate-700'}`}
                      onClick={() => handleAgentSelect(agent)}
                    >
                      <div className="text-sm">{agent.name}</div>
                      <div className="text-xs text-slate-400 font-normal">{agent.email}</div>
                    </div>
                  ))
                ) : (
                  <div className="px-4 py-3 text-sm text-slate-400 text-center">
                    No associates found
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-3 pt-4 border-t border-slate-50 mt-5">
          <button className="bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-700 px-4 py-2 rounded-lg text-sm font-semibold transition-all" onClick={onClose}>Cancel</button>
          <button
            className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-100 disabled:text-slate-400 text-white px-5 py-2 rounded-lg text-sm font-semibold shadow-sm transition-all"
            disabled={selected === currentAgentId || selected == null}
            onClick={() => onConfirm(selected)}
          >
            {currentAgentId ? 'Save Changes' : 'Assign'}
          </button>
        </div>
      </div>
    </div>
  );
}

function LeadCareerAssociateMapping() {
  const [mappings, setMappings] = useState([]);
  const [opsPersons, setOpsPersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filtering States (Raw user inputs vs debounced query values)
  const [searchVal, setSearchVal] = useState('');
  const [search, setSearch] = useState('');
  const [associateSearch, setAssociateSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [serviceFilter, setServiceFilter] = useState('All');
  
  // Pagination States
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  
  const [modal, setModal] = useState({ open: false, leadId: null, currentAgentId: null, leadName: '' });
  
  const isMounted = useRef(false);
  const abortControllerRef = useRef(null);

  const fetchMappings = (
    currentPage = page,
    currentPageSize = pageSize,
    currentSearch = search,
    currentStatus = statusFilter,
    currentAssociate = associateSearch,
    currentService = serviceFilter
  ) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    const queryParams = new URLSearchParams({
      page: currentPage,
      page_size: currentPageSize,
      search: currentSearch,
      status: currentStatus,
      associate: currentAssociate,
      service: currentService
    });
    
    fetch(`/api/lead-career-associate-mappings/?${queryParams.toString()}`, {
      signal: controller.signal
    })
      .then(res => res.json())
      .then(data => {
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
        setMappings(data.results || []);
        setTotalCount(data.count || 0);
        setHasNext(data.has_next || false);
        setHasPrev(data.has_prev || false);
        setLoading(false);
      })
      .catch((err) => {
        if (err.name === 'AbortError') {
          return; // Ignore aborted requests
        }
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
        setError('Failed to load mappings.');
        setLoading(false);
      });
  };

  useEffect(() => {
    // Fetch active ops persons for dropdown (once on mount)
    fetch('/api/active-ops-users/')
      .then(res => res.json())
      .then(data => {
        setOpsPersons(Array.isArray(data.opsUsers) ? data.opsUsers : []);
      })
      .catch(() => {
        setOpsPersons([]);
      });

    // Cleanup abort controller on component unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Debounce searchVal -> search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchVal);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchVal]);

  // Initial mount load
  useEffect(() => {
    fetchMappings(1, pageSize, '', 'All', '', 'All');
    const t = setTimeout(() => {
      isMounted.current = true;
    }, 50);
    return () => clearTimeout(t);
  }, []);

  // Trigger fetch and reset page when debounced search values change
  useEffect(() => {
    if (!isMounted.current) return;
    setPage(1);
    fetchMappings(1, pageSize, search, statusFilter, associateSearch, serviceFilter);
  }, [search]);

  // Trigger fetch and reset page when dropdown filters change
  useEffect(() => {
    if (!isMounted.current) return;
    setPage(1);
    fetchMappings(1, pageSize, search, statusFilter, associateSearch, serviceFilter);
  }, [statusFilter, serviceFilter, associateSearch]);

  // Static list of common lead statuses for filtering
  const statusOptions = ['All', 'New', 'In Progress', 'Active', 'Paused', 'Completed', 'Closed'];

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const handleAgentChange = async (leadId, newOpsPersonId) => {
    if (!newOpsPersonId) return;
    try {
      const resp = await fetch('/api/update-lead-ops-agent-mapping/', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ leadId, opsPersonId: newOpsPersonId })
      });
      if (!resp.ok) throw new Error('Failed to update agent');
      const updated = await resp.json();
      
      // Update the mappings state with the new/updated assignment
      setMappings(prev => prev.map(row =>
        row.lead?.id === leadId ? { 
          ...row, 
          opsPerson: updated.opsPerson,
          opsPersonId: updated.opsPersonId 
        } : row
      ));
      setModal({ open: false, leadId: null, currentAgentId: null, leadName: '' });
    } catch (e) {
      alert('Failed to update agent: ' + (e.message || 'Unknown error'));
    }
  };

  return (
    <PageContainer>
      {/* Title block */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-6 rounded-xl border border-slate-200/80 shadow-sm mb-6">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-slate-800">Lead Mappings</h1>
          </div>
          <p className="text-sm text-slate-500 mt-1 pl-1">
            Map career associates to active leads and manage operational assignments
          </p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white p-6 rounded-xl border border-slate-200/80 shadow-sm mb-6 flex flex-col gap-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search Lead */}
          <div className="relative">
            <label htmlFor="search-lead" className="block text-xs font-bold text-slate-500 uppercase mb-1">Search Lead</label>
            <div className="relative">
              <input
                id="search-lead"
                type="text"
                placeholder="Name or email..."
                value={searchVal}
                onChange={(e) => setSearchVal(e.target.value)}
                className="pl-9 pr-4 py-2 border border-slate-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-lg text-sm w-full transition-all outline-none"
              />
              <div className="absolute left-3 top-2.5 text-slate-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Search Associate */}
          <div>
            <label htmlFor="search-associate" className="block text-xs font-bold text-slate-500 uppercase mb-1">Assigned Associate</label>
            <SearchableSelect
              options={[
                { value: 'unassigned', label: 'Unassigned / NA' },
                ...opsPersons.map((assoc) => ({
                  value: String(assoc.id),
                  label: `${assoc.name} (${assoc.email})`
                }))
              ]}
              value={associateSearch}
              onChange={setAssociateSearch}
              placeholder="All Associates"
            />
          </div>

          {/* Status Filter */}
          <div>
            <label htmlFor="status-filter" className="block text-xs font-bold text-slate-500 uppercase mb-1">Status</label>
            <select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full border border-slate-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 px-3 py-2 rounded-lg text-sm bg-white outline-none transition-all"
            >
              {statusOptions.map(s => (
                <option key={s} value={s}>{s === 'All' ? 'All Statuses' : s}</option>
              ))}
            </select>
          </div>

          {/* Service Filter */}
          <div>
            <label htmlFor="service-filter" className="block text-xs font-bold text-slate-500 uppercase mb-1">Service Opted</label>
            <select
              id="service-filter"
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value)}
              className="w-full border border-slate-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 px-3 py-2 rounded-lg text-sm bg-white outline-none transition-all"
            >
              <option value="All">All Services</option>
              <option value="applications">Applications</option>
              <option value="job-links">Job Links</option>
            </select>
          </div>
        </div>

        <div className="flex justify-between items-center border-t border-slate-100 pt-4 mt-2">
          <div className="text-xs text-slate-400 font-medium">
            Showing {mappings.length > 0 ? (page - 1) * pageSize + 1 : 0}-{Math.min(totalCount, page * pageSize)} of {totalCount} records
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="page-size" className="text-xs font-bold text-slate-500 uppercase">Rows per page:</label>
            <select
              id="page-size"
              value={pageSize}
              onChange={(e) => {
                const newSize = Number(e.target.value);
                setPageSize(newSize);
                setPage(1);
                fetchMappings(1, newSize, search, statusFilter, associateSearch, serviceFilter);
              }}
              className="border border-slate-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 px-2 py-1 rounded-lg text-xs bg-white outline-none transition-all font-semibold text-slate-700"
            >
              {[5, 10, 20, 50].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Main mappings table */}
      <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm mb-6 overflow-hidden">
        <div className="overflow-auto max-h-[500px]">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100 text-slate-600 text-xs font-semibold uppercase tracking-wider">
                <th className="px-6 py-4">Lead Name</th>
                <th className="px-6 py-4">Lead Email</th>
                <th className="px-6 py-4">Lead Status</th>
                <th className="px-6 py-4">Assigned Associate</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700 text-sm">
              {loading ? (
                <tr>
                  <td colSpan="5" className="p-8 text-center">
                    <div className="flex justify-center items-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                    </div>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="5" className="p-8 text-center text-red-500 font-semibold">{error}</td>
                </tr>
              ) : mappings.length === 0 ? (
                <tr>
                  <td colSpan="5" className="p-8 text-center text-slate-400">No mappings found matching current filters.</td>
                </tr>
              ) : (
                mappings.map((row, idx) => (
                  <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-6 py-4 font-semibold text-slate-900">
                      <div className="flex items-center gap-2">
                        <span>{row.lead?.name}</span>
                        {row.lead?.servicesOpted && (
                          (Array.isArray(row.lead.servicesOpted) && row.lead.servicesOpted.includes('job-links')) ||
                          (typeof row.lead.servicesOpted === 'string' && row.lead.servicesOpted.includes('job-links'))
                        ) && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-100 uppercase tracking-wider" title="Opts for Job Board (Job Links)">
                            Job Board
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-500">{row.lead?.email}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                        row.lead?.status?.toLowerCase() === 'active' || row.lead?.status?.toLowerCase() === 'in progress'
                          ? 'bg-green-50 text-green-700 border-green-100'
                          : row.lead?.status?.toLowerCase() === 'paused'
                          ? 'bg-yellow-50 text-yellow-700 border-yellow-100'
                          : row.lead?.status?.toLowerCase() === 'closed'
                          ? 'bg-red-50 text-red-700 border-red-100'
                          : 'bg-slate-50 text-slate-600 border-slate-100'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                          row.lead?.status?.toLowerCase() === 'active' || row.lead?.status?.toLowerCase() === 'in progress'
                            ? 'bg-green-500'
                            : row.lead?.status?.toLowerCase() === 'paused'
                            ? 'bg-yellow-500'
                            : row.lead?.status?.toLowerCase() === 'closed'
                            ? 'bg-red-500'
                            : 'bg-slate-400'
                        }`}></span>
                        {row.lead?.status || 'New'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-600">
                      {row.opsPerson ? (
                        <div>
                          <div className="font-semibold text-slate-900 text-xs">{row.opsPerson.name}</div>
                          <div className="text-[11px] text-slate-400">{row.opsPerson.email}</div>
                        </div>
                      ) : (
                        <span className="text-slate-300 italic">Unassigned</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-900 bg-indigo-50 hover:bg-indigo-100/80 px-2.5 py-1.5 rounded-md text-xs font-semibold transition-all"
                        onClick={() => setModal({
                          open: true,
                          leadId: row.lead?.id,
                          currentAgentId: row.opsPerson?.id || null,
                          leadName: row.lead?.name
                        })}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                        {row.opsPerson ? 'Change' : 'Assign'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {!loading && !error && mappings.length > 0 && (
          <div className="p-4 border-t border-slate-100 flex justify-end items-center gap-2 bg-slate-50/50">
            <button
              className="inline-flex items-center px-3 py-1.5 border border-slate-200 text-slate-700 bg-white hover:bg-slate-50 rounded-lg text-xs font-semibold transition-all disabled:opacity-50 disabled:pointer-events-none active:scale-95 cursor-pointer"
              disabled={!hasPrev}
              onClick={() => {
                const prevPage = Math.max(1, page - 1);
                setPage(prevPage);
                fetchMappings(prevPage, pageSize, search, statusFilter, associateSearch, serviceFilter);
              }}
            >
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).slice(
              Math.max(0, page - 3),
              Math.max(0, page - 3) + 5
            ).map(n => (
              <button
                key={n}
                className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold transition-all border cursor-pointer ${
                  n === page 
                    ? 'bg-indigo-600 border-indigo-600 text-white shadow-sm' 
                    : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                }`}
                onClick={() => {
                  setPage(n);
                  fetchMappings(n, pageSize, search, statusFilter, associateSearch, serviceFilter);
                }}
              >
                {n}
              </button>
            ))}
            <button
              className="inline-flex items-center px-3 py-1.5 border border-slate-200 text-slate-700 bg-white hover:bg-slate-50 rounded-lg text-xs font-semibold transition-all disabled:opacity-50 disabled:pointer-events-none active:scale-95 cursor-pointer"
              disabled={!hasNext}
              onClick={() => {
                const nextPage = Math.min(totalPages, page + 1);
                setPage(nextPage);
                fetchMappings(nextPage, pageSize, search, statusFilter, associateSearch, serviceFilter);
              }}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Modal */}
      <ChangeAssociateModal
        isOpen={modal.open}
        onClose={() => setModal({ open: false, leadId: null, currentAgentId: null, leadName: '' })}
        agents={opsPersons}
        currentAgentId={modal.currentAgentId}
        onConfirm={(newId) => handleAgentChange(modal.leadId, newId)}
        leadName={modal.leadName}
      />
    </PageContainer>
  );
}

export default LeadCareerAssociateMapping;
